/**
 * Created by vsuthar on 8/23/21
 * File: LicenseTable
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useMemo, useState } from 'react'
import { ConnectedTable, TableProvider, ConnectedTableLayout, Paginator } from '@panwds/react-table'
import { Title, Body } from '@panwds/react-ui'
import ProductNameRenderer from './cellRenderer/ProductNameRenderer'
import StatusRenderer from './cellRenderer/StatusRenderer'
import LicenseRenderer from './cellRenderer/LicenseRenderer'
import TenantNameRenderer from './cellRenderer/TenantNameRenderer'
import { setIsGlobalModuleOpen } from '../../../common/utils/Sparky'
import './ProductLicenseSubTenant.scss'
import ExpiryRenderer from './cellRenderer/ExpiryRenderer'
import RegionRenderer from './cellRenderer/RegionRenderer'
import ActionRenderer from './cellRenderer/ActionRenderer'
import { TABLE_HEADER_ACTIONS, TABLE_HEADER_EXPIRATION, TABLE_HEADER_LICENSES, TABLE_HEADER_PRODUCTS, TABLE_HEADER_REGION, TABLE_HEADER_STATUS, TABLE_HEADER_TENANT, TABLE_HEADER_VERTICAL } from '../../constants/LabelConstants'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import TableHeader from '../../../common/components/table/TableHeader'
import styled from 'styled-components'

const TABLE_CONTAINER = styled(V5TableWrapper)`
  [style="min-height: 289px;"] {
    height: auto !important;
  }
`

/**
 *
 * @param title
 * @param instancesAndTreeNode
 * @returns {JSX.Element}
 * @constructor
 */
const ProductLicenseSubTenantTable = ({
  title,
  data,
  dispatch,
  globalDispatch,
  isTableLoading,
  supportedRegionsByKey,
  onDeleteInstance,
  onRenameInstance,
  selectedTreeNode,
  sgKeyBy,
  ...rest
}) => {
  const [pageSize, setPageSize] = useState(25)
  const [pageIndex, setPageIndex] = useState(0)

  const hideGlobalModule = useCallback(() => {
    setIsGlobalModuleOpen(false)
  }, [])

  const columns = useMemo(() => [
    {
      Header: TABLE_HEADER_TENANT,
      sortable: true,
      width: 190,
      accessor: row => row.display_name,
      Cell: TenantNameRenderer,
      id: 'display_name'
    },
    {
      accessor: row => row.vertical,
      Header: TABLE_HEADER_VERTICAL,
      sortable: true,
      width: 120,
      Cell: ({ value }) => <div className='cm-h-full cm-py-2'><Body>{value || ''}</Body></div>
    },
    {
      accessor: row => row.app_id,
      Header: TABLE_HEADER_PRODUCTS,
      width: 180,
      Cell: (props) => <ProductNameRenderer {...{ globalDispatch, hideGlobalModule, ...props }} />,
      sortable: true,
    },
    {
      accessor: row => row.status || row.provisioning_status,
      Header: TABLE_HEADER_STATUS,
      width: 160,
      sortable: true,
      Cell: (props) => <StatusRenderer {...{ globalDispatch, ...props, ...rest }} />
    },
    {
      accessor: row => row.entitlements,
      Header: TABLE_HEADER_LICENSES,
      width: 250,
      sortable: true,
      Cell: LicenseRenderer
    },
    {
      accessor: row => row.license_expiration,
      Header: TABLE_HEADER_EXPIRATION,
      Cell: ExpiryRenderer,
      width: 160,
    },
    {
      accessor: row => row?.platform_region || row?.region,
      Header: TABLE_HEADER_REGION,
      width: 120,
      Cell: props => <RegionRenderer {...{ supportedRegionsByKey, ...props }} />
    },
    {
      accessor: 'actionsColumn',
      Header: TABLE_HEADER_ACTIONS,
      width: 120,
      sticky: true,
      stickyPosition: 'right',
      Cell: (props) => <ActionRenderer row={props.row} onDeleteInstance={onDeleteInstance} onRenameInstance={onRenameInstance}
        selectedTreeNode={sgKeyBy[props?.row?.original?.tsg_id]} />
    },
  ], [globalDispatch, hideGlobalModule, rest, supportedRegionsByKey, onDeleteInstance, onRenameInstance, sgKeyBy])

  const getTitle = useMemo(() => <Title addClassName={'tw-pt-2 tw-pl-2.5 tw-pb-2'} level={2} size={'xxs'} >{title}</Title>, [title])

  const customGetSubRows = (row, relativeIndex) => {
    if (row.id === row.tsg_id) {
      if (Array.isArray(row?.instances)) {
        return row.instances.map(item => ({ ...item, instances: row?.instances }))
      }
    }
    return []
  }

  const expanded = useMemo(() => {
    const map = {}
    const array = data || []
    for (let i = 0; i < array?.length; ++i) {
      map[i] = true
    }
    return map
  }, [data])

  const pages = useMemo(() => {
    const pages = []
    let num = 0
    let end = pageSize
    let start = 0
    let pageInfo = { start }
    let count = 0
    for (const item of data) {
      num += 1
      count += 1
      const instances = Array.isArray(item.instances) ? item.instances : []
      num += instances.length
      if (num > end) {
        pageInfo.end = count
        pageInfo.size = pageInfo.end - pageInfo.start
        pages.push({ ...pageInfo })
        start = count + 1
        pageInfo = { start }
        end = num + pageSize
      }
    }
    pages.push({ start, end: count, size: count - start })
    return pages
  }, [pageSize, data])

  const pageOptions = useMemo(() => pages.map((_, index) => index), [pages])

  const canNextPage = useMemo(() => pageIndex < pages.length - 1, [pages, pageIndex])
  const canPreviousPage = useMemo(() => pageIndex !== 0, [pageIndex])

  const onChangePageSize = useCallback(pageSize => {
    setPageIndex(0)
    setPageSize(pageSize)
  }, [])
  const onNextPage = useCallback(() => setPageIndex(Math.min(pageIndex + 1, pages.length)), [pageIndex, pages.length])
  const onPreviousPage = useCallback(() => setPageIndex(Math.max(0, pageIndex - 1)), [pageIndex])
  const onSelectPage = useCallback(pageIndex => setPageIndex(pageIndex), [])

  const resolvedData = useMemo(() => {
    const start = pages[pageIndex]?.start || 0
    const end = pages[pageIndex]?.end || pageSize
    // console.log('data', start, end, pageIndex, data?.slice(start, end))
    return data?.slice(start, end)
  }, [data, pageIndex, pageSize, pages])

  return (
    <TABLE_CONTAINER>
      <TableProvider
        isLoading={isTableLoading}
        initialState={{ pageSize: 10, pageIndex: 0, expanded }}
        columns={columns}
        data={isTableLoading ? [] : resolvedData}
        enableColumnSort
        enablePagination
        autoResetExpanded
        enableResizeColumns
        manualPagination
        enableColumnOrder
        getSubRows={customGetSubRows}
      >
        <TableHeader {...{ title }} titleProps={{ level: 2, size: 'xxs' }}/>
        <ConnectedTableLayout
          overflow
          sidecar={<div />}
          paginator={(
            <Paginator
              {...{
                pageOptions,
                pageIndex,
                pageSize,
                canNextPage,
                canPreviousPage,
                onChangePageSize,
                onNextPage,
                onPreviousPage,
                onSelectPage
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              pageCount={pageOptions.length}
            />
          )}
        >
          <ConnectedTable />
        </ConnectedTableLayout>
      </TableProvider>
    </TABLE_CONTAINER>
  )
}

ProductLicenseSubTenantTable.propTypes = {}
export default ProductLicenseSubTenantTable
